/**
 * Stateless presenter for the cleanup wizard. All progress state lives in the
 * `usePantryCleanup` hook owned by the parent, so the user can dismiss the
 * dialog mid-run without aborting the work.
 *
 * Props:
 *   open, onOpenChange
 *   mode (string, default "full") – "full" runs name-rydd + images + duplicates,
 *                                   "images" runs only the image fetch.
 *   cleanup ({ state, actions }) – return value from `usePantryCleanup`
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2, Check, X, RefreshCw } from "lucide-react";

export default function PantryCleanupDialog({ open, onOpenChange, mode = "full", cleanup }) {
  const [includeWithImage, setIncludeWithImage] = useState(false);
  const { state, actions } = cleanup;
  const { phase, names, duplicates, imageSuggestions, progress, savingPicks } = state;

  const inProgress = phase === "names" || phase === "images" || phase === "duplicates";

  const startRun = () => {
    actions.runAll({ mode, includeWithImage });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[100vw] max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[88vh] overflow-y-auto rounded-none sm:rounded-lg p-4 sm:p-6" data-testid="pantry-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-violet-600" />
            {mode === "images" ? "Auto-fiks bilder" : "Rydd opp i beholdning"}
          </DialogTitle>
          <DialogDescription>
            {mode === "images"
              ? "Hentar ekte produktbilete frå nett (OpenFoodFacts) for varer som manglar bilde. Du godkjenner forslaga før noko lagras."
              : "Forenklar varenavn (fjernar butikkmerker), hentar ekte produktbilete frå nett, og finn duplikat med AI. Du godkjenner forslaga før noko lagras."}
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="mt-2 space-y-3">
            <ul className="text-sm text-stone-600 space-y-2">
              {mode !== "images" && (
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" /> Forenklar varenavn (t.d. «KIWI Kjøttdeig 14% 500g» → «Kjøttdeig»)</li>
              )}
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" /> Hentar ekte butikkbilete på nett (OpenFoodFacts – gratis)</li>
              {mode !== "images" && (
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" /> AI finn liknande/duplikat-varer du kan slå saman</li>
              )}
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-violet-600 mt-0.5" /> Køyrer i bakgrunnen — du kan lukke vinduet og halde fram med anna arbeid.</li>
            </ul>
            {mode === "images" && (
              <label className="flex items-start gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-stone-50 cursor-pointer text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={includeWithImage}
                  onChange={(e) => setIncludeWithImage(e.target.checked)}
                  className="mt-0.5 rounded border-stone-300"
                  data-testid="cleanup-include-with-image"
                />
                <span>
                  <span className="font-medium">Inkluder varer som allereie har bilde</span>
                  <span className="block text-xs text-stone-500 mt-0.5">Du får då tilbud om å bytte ut eksisterande bilete med eit nytt frå OpenFoodFacts.</span>
                </span>
              </label>
            )}
            <button
              onClick={startRun}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-3 text-base font-semibold text-white hover:bg-violet-700 transition-colors"
              data-testid="cleanup-start-btn"
            >
              <Sparkles className="h-4 w-4" />
              {mode === "images" ? "Start auto-fiks bilder" : "Start opprydding"}
            </button>
          </div>
        )}

        {inProgress && (
          <div className="mt-4 space-y-3" data-testid="cleanup-progress">
            <div className="flex items-center gap-3 text-sm text-stone-700">
              <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
              {phase === "names" && "Forenkler varenavn…"}
              {phase === "images" && (progress.total > 0 ? `Hentar butikkbilete (${progress.done}/${progress.total})…` : "Hentar butikkbilete…")}
              {phase === "duplicates" && "AI søkjer etter duplikat…"}
            </div>
            {phase === "images" && progress.total > 0 && (
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 transition-all" style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }} />
              </div>
            )}
            <p className="text-xs text-stone-400">Du kan lukke vinduet — jobben køyrer vidare i bakgrunnen, og du får varsel når den er klar.</p>
            <button
              onClick={() => onOpenChange(false)}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50"
              data-testid="cleanup-minimize-btn"
            >
              Lukk og hald fram i bakgrunnen
            </button>
          </div>
        )}

        {phase === "review" && (
          <div className="mt-2 space-y-3" data-testid="cleanup-review">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
              {names.count > 0 && <div><strong>{names.count}</strong> namn forenkla{names.merged > 0 ? `, ${names.merged} grupper slått saman` : ""}.</div>}
              {duplicates.groups > 0 && <div><strong>{duplicates.groups}</strong> moglege duplikat funne — sjå dei i hovudvisinga.</div>}
              <div>Vel kva varer som skal få butikkbilde:</div>
            </div>
            <div className="space-y-2">
              {imageSuggestions.map((s) => (
                <div key={s.id} className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${s.picked ? "border-violet-200 bg-violet-50/50" : "border-stone-100 bg-white"}`} data-testid={`cleanup-suggestion-${s.id}`}>
                  <img src={s.image} alt="" className="h-14 w-14 rounded-md object-cover border border-stone-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-stone-800 truncate flex items-center gap-2">
                      <span className="truncate">{s.name}</span>
                      {typeof s.quality === "number" && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${s.quality >= 70 ? "bg-emerald-100 text-emerald-700" : s.quality >= 40 ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`} title="Kvalitet-skår på treffet (Noreg-match + namnelikheit + datakvalitet)">
                          {s.quality >= 70 ? "Høg kvalitet" : s.quality >= 40 ? "Middels" : "Usikker"}
                        </span>
                      )}
                    </div>
                    {s.suggestedName && (
                      <label className="flex items-center gap-1.5 text-xs text-stone-500 mt-0.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!s.useName}
                          onChange={(e) => actions.setUseSuggestedName(s.id, e.target.checked)}
                          className="rounded border-stone-300"
                          data-testid={`cleanup-rename-${s.id}`}
                        />
                        <span>Byt navn til <span className="italic text-stone-700">«{s.suggestedName}»</span></span>
                      </label>
                    )}
                  </div>
                  <button
                    onClick={() => actions.togglePick(s.id)}
                    className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${s.picked ? "bg-violet-600 text-white" : "bg-stone-100 text-stone-400 hover:bg-stone-200"}`}
                    data-testid={`cleanup-toggle-${s.id}`}
                    title={s.picked ? "Hopp over denne" : "Bruk bildet"}
                  >
                    {s.picked ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-white pt-2 -mx-4 px-4 sm:mx-0 sm:px-0 border-t border-stone-100">
              <button
                onClick={() => actions.skipImages()}
                className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
                data-testid="cleanup-skip-images"
              >
                Hopp over
              </button>
              <button
                onClick={() => actions.saveReviewedPicks()}
                disabled={savingPicks}
                className="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 transition-colors disabled:opacity-60"
                data-testid="cleanup-save-images"
              >
                {savingPicks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Lagre valde
              </button>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="mt-4 space-y-3 text-center" data-testid="cleanup-done">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800">Ferdig!</h3>
            <ul className="text-sm text-stone-600 space-y-1">
              {names.count > 0 && <li>✓ {names.count} varenavn forenkla{names.merged > 0 ? ` (${names.merged} grupper slått saman)` : ""}</li>}
              <li>✓ {imageSuggestions.filter((s) => s.picked).length} butikkbilete lagt til</li>
              {duplicates.groups > 0 && <li>✓ {duplicates.groups} mogleg duplikat-grupper funne (sjekk hovudvisinga)</li>}
            </ul>
            <div className="flex items-center justify-center gap-2 mt-3">
              <button
                onClick={() => actions.reset()}
                className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                data-testid="cleanup-rerun"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Køyr på nytt
              </button>
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-md bg-navy-800 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-900 transition-colors"
                data-testid="cleanup-close"
              >
                Lukk
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
